import { sanitizeTelemetryAttributes, type SafeTelemetryPrimitive } from "./telemetry-privacy";

type TelemetryOutcome = "success" | "partial" | "failure";
type TelemetryEventDefinition = Readonly<{
  "route.viewed": Readonly<{ route: string }>;
  "readModel.loaded": Readonly<{ code?: string }>;
  "section.degraded": Readonly<{ section: string; code?: string }>;
  "unexpected.error": Readonly<{ code: string }>;
  "organization.switched": undefined;
  "locale.changed": undefined;
  "theme.changed": undefined;
}>;

export type TelemetryEventName = keyof TelemetryEventDefinition;

type TelemetryEventFor<TName extends TelemetryEventName> = Readonly<{
  name: TName;
  outcome?: TelemetryOutcome;
  correlationId?: string;
}> & (TelemetryEventDefinition[TName] extends undefined
  ? Readonly<{ attributes?: never }>
  : Readonly<{ attributes?: TelemetryEventDefinition[TName] }>);

export type TelemetryEvent = {
  [TName in TelemetryEventName]: TelemetryEventFor<TName>;
}[TelemetryEventName];

export type TelemetrySink = Readonly<{ emit(event: TelemetryEvent): void }>;

/**
 * Runtime defense in depth for telemetry. TypeScript prevents normal callers
 * from adding arbitrary attributes; this sink-side barrier also protects
 * against unsafe casts and future untyped integrations.
 */
export const sanitizeTelemetryEvent = (event: TelemetryEvent): TelemetryEvent => {
  const rawAttributes = (event as Readonly<{ attributes?: Readonly<Record<string, SafeTelemetryPrimitive>> }>).attributes;
  if (rawAttributes === undefined) return event;

  const safeAttributes = sanitizeTelemetryAttributes(event.name, rawAttributes);

  const base = {
    name: event.name,
    ...(event.outcome === undefined ? {} : { outcome: event.outcome }),
    ...(event.correlationId === undefined ? {} : { correlationId: event.correlationId }),
  };
  return (safeAttributes === undefined ? base : { ...base, attributes: safeAttributes }) as TelemetryEvent;
};

export const createDevelopmentTelemetry = (): TelemetrySink => ({
  emit: (event) => {
    if (!import.meta.env.DEV) return;
    const safe = sanitizeTelemetryEvent(event);
    console.info(
      "[nave:telemetry]",
      safe.name,
      safe.outcome ?? "",
      (safe as Readonly<{ attributes?: Readonly<Record<string, SafeTelemetryPrimitive>> }>).attributes ?? {},
    );
  },
});

export const telemetry = createDevelopmentTelemetry();
